import React, { useEffect, useState } from 'react'
import * as yup from 'yup'
import { InferType } from 'yup'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useDebounceValue, useLocalStorage } from 'usehooks-ts'
import { SocietyInvites } from '../../components/society/SocietyInvite/SocietyInvites'
import { LockedSocietiesCallout } from '../../components/society/LockedSocietiesCallout'
import { FeedContainer } from './FeedContainer'
import { Container } from '../../components/common/Container'
import { useIsMobile } from '../../hooks/useIsMobile'
import { SearchBar } from '../../components/common/SearchBar'
import { Button, ButtonVariant } from '../../components/common/Button'
import { IconChoices } from '../../components/common/Icon'
import { useAppTranslation } from '../../hooks/useAppTranslation'
import { theme } from '../../theme/theme'
import { requestQueries } from '../../api/requests'
import { postRowQueries } from '../../api/posts'
import { societyQueries } from '../../api/societies'
import { getSocietiesWhereUserCanPost } from './useUserFeedSocieties'
import { isUserAdminInSociety } from '../../helpers/society'
import { useModal } from '../../hooks/useModal'
import { ModalHeader } from '../../components/common/Modal/ModalHeader'
import { Modal } from '../../components/common/Modal'
import { ModalBody } from '../../components/common/Modal/ModalBody'
import { PostFilterDropdownOption } from '../../components/feed/PostFilterDropdownOption'
import { useAuth } from '../../hooks/useAuth'
import { StatusMessages } from '../../components/status/StatusMessages'
import GetToKnow from '../../components/society/GetToKnow/GetToKnow'
import { i18nSocietyType } from '../../i18n'
import { useCurrentSociety } from '../../hooks/useCurrentSociety'

export const postFilterSchema = yup
  .object({
    postTypes: yup.array(yup.string()).required(),
    societies: yup.array(yup.string()).required(),
  })
  .required()

const FeedView = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { userId } = useAuth()
  const isMobile = useIsMobile()
  const [searchTerm, setSearchTerm] = useState('')
  const [debounceSearchTerm] = useDebounceValue(searchTerm, 300)
  const { data: requests, isLoading: isRequestsLoading } = useQuery(
    requestQueries.getRequestsPerUser(userId)
  )
  const { society: currentSociety } = useCurrentSociety()
  const {
    show: showFilterModal,
    open: openFilterModal,
    close: closeFilterModal,
  } = useModal({ preventClose: true })

  const [filterSocieties, setFilterSocietiesStorage] = useLocalStorage<
    string[]
  >(`feedview-filterSocieties-${userId}`, [])

  const [filterPostTypes, setFilterPostStorage] = useLocalStorage<string[]>(
    `feedview-filterPostType-${userId}`,
    []
  )

  const showPinned = !debounceSearchTerm && filterPostTypes.length === 0

  const {
    data: feed,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending: isFeedPending,
    isFetching,
    isError: isFeedError,
    refetch: refetchFeed,
  } = useInfiniteQuery(
    postRowQueries.feed({
      societies: filterSocieties,
      postTypes: filterPostTypes,
      searchTerm: debounceSearchTerm,
    })
  )

  const { data: pinnedPosts } = useQuery({
    ...postRowQueries.pinned(filterSocieties),
    enabled: showPinned,
  })

  const pages = feed?.pages
  useEffect(() => {
    if (
      pages?.at(-1)?.items.length === 0 &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isFeedError
    ) {
      fetchNextPage()
    }
  }, [pages, hasNextPage, isFetchingNextPage, isFeedError, fetchNextPage])

  const { data: societies, isLoading: isSocietiesLoading } = useQuery(
    societyQueries.getUserSocieties(userId)
  )
  const { control, setValue, watch, handleSubmit } = useForm({
    resolver: yupResolver(postFilterSchema),
    defaultValues: {
      postTypes: filterPostTypes,
      societies: filterSocieties,
    },
  })

  const watchSocieties = watch('societies')
  const watchPostTypes = watch('postTypes')

  const onCustomChange = (name: string, checked: boolean): void => {
    // If all is selected deselect all suboptions
    if (name === 'all' && checked) {
      setValue('postTypes', [])
      return
    }
    if (name !== 'all' && checked) {
      setValue('postTypes', [...watchPostTypes, name])
      return
    }

    // If a suboption is deselected remove from array
    if (!checked) {
      const _postTypes = watchPostTypes.filter(
        (_postType) => _postType !== name
      )
      setValue('postTypes', _postTypes)
    }
  }
  const onCustomSocietyChange = (id: string, checked: boolean): void => {
    // If all is selected deselect all suboptions
    if (id === 'allSocieties' && checked) {
      setValue('societies', [])
      return
    }
    // If a suboption is selected disable allSocieties
    if (id !== 'allSocieties' && checked) {
      setValue('societies', [...watchSocieties, id])
      return
    }

    // If a suboption is deselected remove from array
    if (!checked) {
      const _societies = watchSocieties.filter((_society) => _society !== id)
      setValue('societies', _societies)
    }
  }

  if (!userId) return null
  const isLoading = isFeedPending || isSocietiesLoading || isRequestsLoading
  const isFilterActive = !!filterSocieties.length || !!filterPostTypes.length

  const posts = feed?.pages.flatMap((page) => page.items) || []

  const pinned = showPinned ? pinnedPosts || [] : []
  const pinnedIds = new Set(pinned.map((post) => post.id))
  const combinedPosts = [
    ...pinned,
    ...posts.filter((post) => !pinnedIds.has(post.id)),
  ]

  const isPendingRequestView = !societies?.length && !!requests?.length
  const noSocieties = !societies?.length
  const societiesWhereUserCanPost = getSocietiesWhereUserCanPost({
    userId,
    societies: societies || [],
  })
  const lockedSocietiesForUser = societiesWhereUserCanPost.filter(
    (society) => isUserAdminInSociety(society, userId) && society.locked
  )

  const allSocietiesLockedForUser =
    societiesWhereUserCanPost.length === lockedSocietiesForUser.length
  const isAllSocietiesLockedForUserErrorView =
    societiesWhereUserCanPost.length > 0 && allSocietiesLockedForUser

  const onSubmit = (formValue: InferType<typeof postFilterSchema>): void => {
    const postTypeFilter = formValue.postTypes.flatMap(
      (postType) => postType || []
    )
    const societiesFilter = formValue.societies.flatMap(
      (society) => society || []
    )
    setFilterSocietiesStorage(societiesFilter)
    setFilterPostStorage(postTypeFilter)

    closeFilterModal()
  }
  return (
    <>
      <div className="flex h-full flex-1 flex-col items-center overflow-y-scroll bg-gray-100">
        <SocietyInvites />
        <Container
          className="pb-0 md:max-w-4xl md:pb-0"
          overflowScrollY={false}
        >
          <LockedSocietiesCallout />
          <StatusMessages authenticated />

          <GetToKnow
            title={translate('getToKnowScreen.screens.feed.title')}
            className="w-full mb-4"
            description={translate('getToKnowScreen.screens.feed.description')}
            getToKnowId="feed"
          />
          <div className="mb-3 flex justify-end gap-3 flex-wrap-reverse">
            <SearchBar
              className="flex-grow"
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              value={searchTerm}
            />
            <Button
              label={
                isMobile ? undefined : translate('feedView.filterFeed.title')
              }
              disableTabIndex
              onClick={openFilterModal}
              iconPlacement="left"
              icon={IconChoices.FILTER}
              iconColor={isFilterActive ? theme.colors.brandGreen : 'black'}
            />
          </div>
          <FeedContainer
            userId={userId}
            isAllSocietiesLockedForUserErrorView={
              isAllSocietiesLockedForUserErrorView
            }
            fetchNextPage={() => fetchNextPage({ cancelRefetch: false })}
            isFilterActive={isFilterActive || !!debounceSearchTerm}
            posts={combinedPosts}
            noSocieties={noSocieties}
            isPendingRequestView={isPendingRequestView}
            societiesWhereUserCanPost={societiesWhereUserCanPost}
            isLoading={isLoading}
            isFetching={isFetching}
            hasFeedError={isFeedError && !feed}
            retryFeed={refetchFeed}
          />
        </Container>
      </div>
      <Modal show={showFilterModal} size="sm" customSize="400px">
        {{
          header: (
            <ModalHeader onClose={closeFilterModal}>
              <div>
                {translate('feedView.filterFeed.modalTitle')}
                <div style={{ ...theme.textVariants.base }}>
                  {translate('feedView.filterFeed.subtitle')}
                </div>
              </div>
            </ModalHeader>
          ),
          body: (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex h-full min-h-0 flex-col"
            >
              <ModalBody className="min-h-0 max-h-[calc(100vh-14rem)] md:max-h-[440px]">
                <div style={{ ...theme.textVariants.baseBold }}>
                  {translate('feedView.filterFeed.postsFilterTitle')}
                </div>
                <Controller
                  control={control}
                  name="postTypes"
                  render={({ field: { onChange } }) => (
                    <PostFilterDropdownOption
                      name="all"
                      onChange={onChange}
                      onCustomChange={onCustomChange}
                      label={translate(
                        'feedView.filterFeed.postsFilterAllOptionValue'
                      )}
                      value={!watchPostTypes.length}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="postTypes"
                  render={({ field: { onChange } }) => (
                    <PostFilterDropdownOption
                      name="board-member"
                      onChange={onChange}
                      onCustomChange={onCustomChange}
                      label={translate(
                        'feedView.filterFeed.postsFilterBoardOptionValue',
                        {
                          context: i18nSocietyType(
                            currentSociety?.societyTypeDisplayName
                          ),
                        }
                      )}
                      value={watchPostTypes.includes('board-member')}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="postTypes"
                  render={({ field: { onChange } }) => (
                    <PostFilterDropdownOption
                      name="resident"
                      onChange={onChange}
                      onCustomChange={onCustomChange}
                      label={translate(
                        'feedView.filterFeed.postsFilterNeighboursOptionValue',
                        {
                          context: i18nSocietyType(
                            currentSociety?.societyTypeDisplayName
                          ),
                        }
                      )}
                      value={watchPostTypes.includes('resident')}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="postTypes"
                  render={({ field: { onChange } }) => (
                    <PostFilterDropdownOption
                      name="isAlert"
                      onChange={onChange}
                      onCustomChange={onCustomChange}
                      label={translate(
                        'feedView.filterFeed.postsFilterImportantOptionValue'
                      )}
                      value={watchPostTypes.includes('isAlert')}
                    />
                  )}
                />
                <div style={{ ...theme.textVariants.baseBold }}>
                  {translate('feedView.filterFeed.societyFilterTitle', {
                    context: i18nSocietyType(
                      currentSociety?.societyTypeDisplayName
                    ),
                  })}
                </div>
                <Controller
                  name="societies"
                  control={control}
                  render={({ field: { onChange } }) => (
                    <PostFilterDropdownOption
                      name="allSocieties"
                      onChange={onChange}
                      onCustomChange={onCustomSocietyChange}
                      label={translate(
                        'feedView.filterFeed.postsFilterAllOptionValue'
                      )}
                      value={!watchSocieties.length}
                    />
                  )}
                />
                {societies?.map((society) => {
                  return (
                    <Controller
                      key={society._id}
                      control={control}
                      name="societies"
                      render={({ field: { onChange } }) => (
                        <PostFilterDropdownOption
                          name={society._id}
                          onChange={onChange}
                          onCustomChange={onCustomSocietyChange}
                          label={society.name}
                          value={watchSocieties.includes(society._id)}
                        />
                      )}
                    />
                  )
                })}
              </ModalBody>
              <div className="shrink-0 p-6">
                <Button
                  wrapperClassName="block w-full"
                  style={{ width: '100%' }}
                  variant={ButtonVariant.PRIMARY}
                  type="submit"
                  label={translate('common.actions.confirm')}
                />
              </div>
            </form>
          ),
        }}
      </Modal>
    </>
  )
}

export { FeedView }
